import React, {useCallback, useContext, useEffect, useState} from 'react';

import {ErrorBoundary} from '@sentry/react';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {Tabs3Level} from 'src/components/tabs-3-level';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {PricingModel, PricingModel as PricingModels} from 'src/openapi/admin/models/PricingModel';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {Tab} from '@pb/kit-component-tabs-v2';
import Typography from '@pb/kit-component-typography';

import {API_CALLS_TABS} from '../constants';
import {useCallCosts} from '../useCallCosts';
import {useDefaultCallCost} from '../useDefaultCallCost';
import {isDefaultCallCosts} from '../utils/isDefaultCallCosts';
import {restoreDefaultCallCost} from '../utils/restoreDefaultCallCost';
import {CallCostTable} from '../сomponents/call-cost-table';


type Props = {
    accountId: number,
    onTabChange: (tab: string) => void,
};

export const PayPerCallTab = ({accountId, onTabChange}: Props) => {
    const {accountFieldChange, accountSubmit} = useEventTracking();
    const [busy, setBusy] = useState(false);
    const {canEditApiV2} = useAdminAccessRight();

    const {
        isLoading: isDefaultCallCostLoading,
        loadingError: defaultCallCostLoadingError,
        data: defaultCallCost,
    } = useDefaultCallCost({accountId, pricingModel: PricingModel.PAY_PER_CALL});

    const {
        isLoading,
        loadingError,

        data: costGroups,
        setData: setCallsCosts,
        isChanged,
        loadData,

        saveChanges,
        discardChanges,
    } = useCallCosts({accountId, pricingModel: PricingModels.PAY_PER_CALL});


    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {isCurrentChunkChanged, proceed, setIsChunkChanged} = useConfirmationChunk({
        chunkName: 'api-calls-pay-per-call',
        onProceed: () => {
            loadData();
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    useEffect(() => {
        setIsChunkChanged(isChanged);
    }, [isChanged, setIsChunkChanged]);

    const saveForm = useCallback(async () => {
        accountSubmit({place: 'API V2 - Calls - Subscription', submit: true});

        const calls = [];

        if (isChanged) {
            calls.push(saveChanges());
        }

        setBusy(true);
        const errors = [
            ...new Set((await Promise.all(calls)).map(error => error).filter(Boolean)),
        ];
        setBusy(false);

        if (errors.length) {
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        isChanged,
        saveChanges,
        accountSubmit,
        proceed,
        setErrorMessage,
    ]);

    const discardForm = useCallback(() => {
        accountSubmit({place: 'API V2 - Calls - Pay Per Call', submit: false});

        discardChanges();
    }, [discardChanges, accountSubmit]);

    const restoreDefaultSettings = useCallback(() => {
        setCallsCosts(prevState => ({
            ...prevState,
            callsCosts: restoreDefaultCallCost(prevState.callsCosts, defaultCallCost),
        }));
    }, [defaultCallCost, setCallsCosts]);

    const editDisabled = !canEditApiV2(accountId);

    return (
        <ErrorBoundary fallback={
            <ErrorPage subtitle="Please try again later." title="Pay per call view is unavailable"/>
        }>
            <Tabs3Level activeTab={API_CALLS_TABS.PAY_PER_CALL.name} setActiveTab={onTabChange}>
                <Tab value={API_CALLS_TABS.SUBSCRIPTION.name} {...API_CALLS_TABS.SUBSCRIPTION}/>
                <Tab value={API_CALLS_TABS.PAY_PER_CALL.name} {...API_CALLS_TABS.PAY_PER_CALL}/>
            </Tabs3Level>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            Pay Per Call
                        </Typography>
                        {!editDisabled && (
                            <Group>
                                <Button
                                    variant="Type.TERTIARY"
                                    onClick={restoreDefaultSettings}
                                    disabled={isDefaultCallCosts(costGroups.callsCosts, defaultCallCost)}>
                                    Use default settings
                                </Button>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={discardForm}
                                    disabled={!isCurrentChunkChanged}>
                                    Discard Changes
                                </Button>
                                <Button
                                    variant="Type.PRIMARY"
                                    busy={busy}
                                    onClick={saveForm}
                                    disabled={!isCurrentChunkChanged}>
                                    Save Changes
                                </Button>
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
                <CallCostTable
                    isLoading={isLoading || isDefaultCallCostLoading}
                    loadingError={loadingError || defaultCallCostLoadingError}
                    callsCosts={costGroups.callsCosts}
                    defaultCallCosts={defaultCallCost}
                    onChange={costs => {
                        accountFieldChange({
                            parameterName: 'Pay Per Call Calls',
                            section: 'API V2',
                            viewType: 'Existed',
                        });
                        setCallsCosts({...costGroups, callsCosts: costs});
                    }}
                    disabled={editDisabled}
                />
                <Group direction="v" gap="xxs">
                    <span>Created by: {costGroups.changesInfo.createdBy} {costGroups.changesInfo.createdAt}</span>
                    <span>Last edited by: {costGroups.changesInfo.updatedBy} {costGroups.changesInfo.updatedAt}</span>
                </Group>
            </ProfileMain>
        </ErrorBoundary>
    );
};
